import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../context/AuthContext";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { LogOut, FileText, Users, Plus, Download, Trash2, Pencil, ShieldCheck, CarFront, ClipboardList } from "lucide-react";
import PermitForm from "../components/PermitForm";
import PermitsList from "../components/PermitsList";
import UsersManager from "../components/UsersManager";

const LOGO_URL = "https://ixcateopandecuauhtemocgro.gob.mx/vendor/municipio/assets/img/logo.png";
const ESCUDO_URL = "https://ixcateopandecuauhtemocgro.gob.mx/vendor/municipio/assets/img/escudo.png";

export default function Dashboard() {
  const { user, logout, refreshUser, authHeaders } = useAuth();
  const [permits, setPermits] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingPermits, setLoadingPermits] = useState(false);
  const [tab, setTab] = useState(user?.role === "admin" ? "create" : "create");

  const isAdmin = user?.role === "admin";
  const remaining = Math.max((user?.permits_quota || 0) - (user?.permits_used || 0), 0);

  const loadPermits = async () => {
    setLoadingPermits(true);
    try {
      const res = await axios.get(`${API}/permits`, { headers: authHeaders });
      setPermits(res.data);
    } catch (e) {
      toast.error("No se pudieron cargar los permisos");
    } finally {
      setLoadingPermits(false);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, { headers: authHeaders });
      setUsers(res.data);
    } catch (e) {
      toast.error("No se pudieron cargar los usuarios");
    }
  };

  useEffect(() => {
    loadPermits();
    if (isAdmin) loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePermitCreated = async (permit) => {
    toast.success(`Permiso ${permit.folio_short} creado correctamente`);
    await refreshUser();
    await loadPermits();
    setTab("list");
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f5f6f8" }}>
      {/* Header */}
      <header style={{ backgroundColor: "#fff", borderBottom: "1px solid #e5e7eb", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
        <div className="dash-header" style={{ maxWidth: 1400, margin: "0 auto", padding: "0.75rem 1rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: "1 1 auto" }}>
            <img src={LOGO_URL} alt="" style={{ height: 48, flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, color: "#1f2937", fontSize: "0.95rem", lineHeight: 1.15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Dirección de Tránsito</div>
              <div style={{ fontSize: "0.72rem", color: "#6b7280", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Ixcateopan de Cuauhtémoc, Gro.</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontWeight: 600, color: "#1f2937", fontSize: "0.88rem", lineHeight: 1.1 }}>{user?.name}</div>
              <div style={{ fontSize: "0.72rem", color: "#6b7280", display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end", marginTop: 2 }}>
                {isAdmin ? <Badge style={{ backgroundColor: "#f58238", color: "white" }}><ShieldCheck size={12} style={{ marginRight: 4 }} />Admin</Badge> : <Badge variant="secondary">Usuario</Badge>}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={logout}>
              <LogOut size={14} style={{ marginRight: 6 }} /> <span className="hide-sm">Salir</span>
            </Button>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 1400, margin: "0 auto", padding: "1rem" }}>
        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 20 }}>
          <Card>
            <CardContent style={{ padding: "1.25rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: "0.78rem", color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5 }}>Permisos disponibles</div>
                <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#f58238" }}>{isAdmin ? "∞" : remaining}</div>
              </div>
              <div style={{ backgroundColor: "#fef3eb", padding: 10, borderRadius: 12 }}>
                <ClipboardList color="#f58238" size={24} />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent style={{ padding: "1.25rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: "0.78rem", color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5 }}>Permisos emitidos</div>
                <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1f2937" }}>{permits.length}</div>
              </div>
              <div style={{ backgroundColor: "#eef2ff", padding: 10, borderRadius: 12 }}>
                <FileText color="#4f46e5" size={24} />
              </div>
            </CardContent>
          </Card>
          {isAdmin && (
            <Card>
              <CardContent style={{ padding: "1.25rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: "0.78rem", color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5 }}>Usuarios</div>
                  <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1f2937" }}>{users.length}</div>
                </div>
                <div style={{ backgroundColor: "#ecfdf5", padding: 10, borderRadius: 12 }}>
                  <Users color="#059669" size={24} />
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList style={{ marginBottom: 20 }}>
            <TabsTrigger value="create"><Plus size={14} style={{ marginRight: 6 }} />Nuevo permiso</TabsTrigger>
            <TabsTrigger value="list"><FileText size={14} style={{ marginRight: 6 }} />Permisos emitidos</TabsTrigger>
            {isAdmin && <TabsTrigger value="users"><Users size={14} style={{ marginRight: 6 }} />Usuarios</TabsTrigger>}
          </TabsList>

          <TabsContent value="create">
            <PermitForm
              disabled={!isAdmin && remaining <= 0}
              remaining={remaining}
              isAdmin={isAdmin}
              onCreated={handlePermitCreated}
            />
          </TabsContent>

          <TabsContent value="list">
            <PermitsList permits={permits} loading={loadingPermits} onReload={loadPermits} isAdmin={isAdmin} />
          </TabsContent>

          {isAdmin && (
            <TabsContent value="users">
              <UsersManager users={users} onReload={loadUsers} />
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
}
